import { useEffect, useMemo, useRef, useState } from "react";
import { createPageSchema, formatPhone, patientIssues, serviceLabel, type Picklists, type Team, type Urgency } from "@pagelink/shared";
import { api, ApiError, type Actor, type OnCallEntry } from "./api.ts";
import { Avatar, Banner, Icon, divColor } from "./ui.tsx";

// Field names as the form shows them, for the "still needed" line.
const FIELD_NAMES: Record<string, string> = {
  service: "service", consultTypeId: "consult type", unitId: "patient location",
  bed: "a valid bed", callback: "callback number",
};

type Who = "people" | "teams" | "service";

/* ---------------------------------------------------------------------------
 * New page. Two ways to reach someone, never both at once: you either know
 * the person or you know the service. What a page carries is what the schema
 * allows: who, which service, how urgent, what kind of consult, where, and a
 * number to call back. No patient identifier, no free text.
 * ------------------------------------------------------------------------ */
export function Compose({ me, initialService, initialSub = null, initialTeam = null, initialTo = null, patientData = false, onSent, onCancel, onOpenExisting }: {
  me: Actor; initialService?: string; initialSub?: string | null; initialTeam?: string | null; initialTo?: string | null; patientData?: boolean;
  onSent: (id: string) => void; onCancel: () => void; onOpenExisting: (id: string) => void;
}) {
  const [onCall, setOnCall] = useState<OnCallEntry[]>([]);
  const [lists, setLists] = useState<Picklists | null>(null);
  const [who, setWho] = useState<Who>(initialTeam ? "teams" : initialService ? "service" : "people");
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [rq, setRq] = useState("");
  // The division, and optionally one of its services: "ID, OPAT".
  const [service, setService] = useState(initialService ?? "");
  const [subService, setSubService] = useState<string | null>(initialService ? initialSub : null);
  const [toId, setToId] = useState<string | null>(null);
  // Routine unless the sender says otherwise: STAT is a deliberate tap.
  const [urgency, setUrgency] = useState<Urgency | "">("Routine");
  const [consultTypeId, setConsultTypeId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [bed, setBed] = useState("");
  const [callback, setCallback] = useState(me.callback ?? "");
  // Patient-data mode only (the demo deployment).
  const [patientIdType, setPatientIdType] = useState<"MRN" | "PHN">("MRN");
  const [patientId, setPatientId] = useState("");
  const [patientInitials, setPatientInitials] = useState("");
  const [question, setQuestion] = useState("");
  const [context, setContext] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<string | null>(null);
  const [tried, setTried] = useState(false);

  useEffect(() => {
    api.onCall().then((r) => {
      setOnCall(r.services);
      // Arrived from "Page" on a person in the on-call search.
      const e = initialTo ? r.services.find((x) => x.id === initialTo) : undefined;
      if (e) { setToId(e.id); setService(e.service); setSubService(e.sub_service); }
    }).catch(() => setOnCall([]));
    api.teams().then((r) => {
      setTeams(r.teams);
      const t = r.teams.find((x) => x.id === initialTeam);
      if (t) { setTeamId(t.id); setService(t.name); setSubService(null); }
    }).catch(() => setTeams([]));
    api.picklists().then(setLists).catch(() => setLists({ units: [], consultTypes: [], outcomes: [], services: [], subServices: [], feeCodes: [] }));
  }, []);

  const shortOf = useMemo(() => new Map((lists?.services ?? []).map((s) => [s.name, s.short])), [lists]);
  const label = (div: string, sub: string | null) => serviceLabel(div, sub, shortOf.get(div));

  // Every division the hospital lists, grouped, each followed by its services
  // ("ID, OPAT"), plus any division on the rota that the list does not have
  // yet, so nothing on call can become unpageable. A division can be paged as
  // a whole or through one of its services.
  const serviceGroups = useMemo(() => {
    const listed = lists?.services ?? [];
    const names = new Set(listed.map((s) => s.name));
    const extra = Array.from(new Set(onCall.map((e) => e.service))).filter((n) => !names.has(n)).sort();
    const all = [...listed, ...extra.map((name) => ({ name, grp: "Other", short: null }))];
    const subs = lists?.subServices ?? [];
    return Array.from(new Set(all.map((s) => s.grp))).map((g) => ({
      grp: g,
      items: all.filter((s) => s.grp === g).flatMap((s) => [
        { division: s.name, sub: null as string | null, text: s.name, covered: coverFor(onCall, s.name, null).length > 0 },
        ...subs.filter((x) => x.division === s.name).map((x) => ({
          division: s.name, sub: x.name as string | null, text: serviceLabel(s.name, x.name, s.short),
          covered: coverFor(onCall, s.name, x.name).length > 0,
        })),
      ]),
    }));
  }, [lists, onCall]);
  const team = coverFor(onCall, service, subService);
  const target = service ? label(service, subService) : "";
  const consultTypes = (lists?.consultTypes ?? []).filter((c) => !c.service || c.service === service);
  const recipient = toId ? onCall.find((e) => e.id === toId) ?? null : null;
  // The team being paged, when the Teams tab is in use.
  const group = who === "teams" && teamId ? teams.find((t) => t.id === teamId) ?? null : null;
  const reach = group ? group.members.filter((m) => m.id !== me.id).length : 0;
  const gap = who === "service" && !!service && team.length === 0;

  const hits = useMemo(() => {
    const t = rq.trim().toLowerCase();
    if (!t) return [];
    const seen = new Set<string>();
    return onCall
      .filter((e) => e.id !== me.id && !seen.has(e.id) && seen.add(e.id))
      .filter((e) => [e.name, e.title, e.service, e.sub_service ?? "", label(e.service, e.sub_service)].join(" ").toLowerCase().includes(t))
      .slice(0, 6);
  }, [rq, onCall, me.id]);

  const pickPerson = (e: OnCallEntry) => {
    setToId(e.id);
    setTeamId(null);
    setService(e.service);
    setSubService(e.sub_service);
    setConsultTypeId("");
    setRq("");
  };
  const pickService = (v: string) => {
    const [div = "", sub = ""] = v.split(SEP);
    setService(div);
    setSubService(sub || null);
    setToId(null);
    setTeamId(null);
    setConsultTypeId("");
  };

  // Validate with the same schema the server uses, so the two can never differ.
  const candidate = (force: boolean) => ({
    toId: group ? null : toId, teamId: group?.id ?? null, service, subService, urgency: urgency || "Routine", consultTypeId, unitId, bed, callback, force,
    context: patientData ? null : context,
    ...(patientData
      ? { patientIdType: consultTypeId === "general" ? null : patientIdType, patientId: consultTypeId === "general" ? null : patientId,
          patientInitials: consultTypeId === "general" ? null : patientInitials, question }
      : {}),
  });
  const parsed = createPageSchema.safeParse(candidate(false));
  const issues = parsed.success ? {} : parsed.error.flatten().fieldErrors;
  const issue = (k: string) => (tried ? issues[k as keyof typeof issues]?.[0] : undefined);

  const missing: string[] = [];
  if (!toId && !gap && !group) missing.push("recipient");
  if (!consultTypeId) missing.push("consult type");
  if (!urgency) missing.push("urgency");
  if (!callback) missing.push("callback number");
  if (patientData) {
    const pi = patientIssues({ consultTypeId, patientIdType: consultTypeId === "general" ? null : patientIdType,
      patientId: patientId || null, patientInitials: patientInitials || null, question: question.trim() || null });
    if (pi.question) missing.push("clinical question");
    if (pi.patientId) missing.push(patientIdType === "PHN" ? "a valid PHN" : "a valid MRN");
    if (pi.patientInitials) missing.push("patient initials");
  }
  for (const k of Object.keys(issues)) {
    const n = FIELD_NAMES[k] ?? k;
    if (!missing.includes(n) && k !== "service") missing.push(n);
  }
  const ready = missing.length === 0 && !!urgency && parsed.success;

  const send = async (force = false) => {
    setTried(true);
    if (!ready || busy) return;
    const p = createPageSchema.safeParse(candidate(force));
    if (!p.success) return;
    setBusy(true);
    setError(null);
    setDuplicate(null);
    try {
      const { page } = await api.createPage(p.data);
      onSent(page.id);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setDuplicate((e.data as { existingPageId?: string })?.existingPageId ?? null);
        setError(e.message);
      } else {
        setError(e instanceof ApiError ? e.message : "Could not send the page.");
      }
    } finally {
      setBusy(false);
    }
  };

  const toLabel = group ? `${group.name} · ${reach}` : gap ? "Locating" : recipient?.name ?? "";
  const uWord = urgency === "STAT" ? "STAT" : urgency ? "routine" : "";
  const sendLabel = busy ? "Sending page…" : ready ? `Send ${uWord} page to ${toLabel}` : "Send page";
  const unit = (lists?.units ?? []).find((u) => u.id === unitId);

  return (
    <>
      <div className="phead">
        <button className="icon-btn" onClick={onCancel} aria-label="Cancel and go back"><Icon name="back" size={19} sw={2} /></button>
        <h1>Page someone</h1>
      </div>
      <div className="psummary" aria-live="polite">
        <span className={`s${service ? " on" : ""}`}>{group ? "Group" : target || "Service"}</span>
        <span className="arrow" aria-hidden="true">→</span>
        <span className={`s${toLabel ? " on" : ""}`}>{toLabel || "Recipient"}</span>
        <span className="arrow" aria-hidden="true">→</span>
        <span className={`s${urgency ? " on" : ""}${urgency === "STAT" ? " stat" : ""}`}>{urgency || "Urgency"}</span>
      </div>

      <div className="form">
        {error && (
          <Banner kind="err">
            {error}
            {duplicate && (
              <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                <button className="btn soft sm" onClick={() => onOpenExisting(duplicate)}>Open the existing page</button>
                <button className="btn soft sm" onClick={() => void send(true)} disabled={busy}>Different patient, send anyway</button>
              </div>
            )}
          </Banner>
        )}

        <div className="fsec">
          <span className="flabel">Who are you paging?</span>
          <div className="rtabs" role="tablist" aria-label="How to choose a recipient">
            {(["people", "service", "teams"] as Who[]).map((k) => (
              <button key={k} role="tab" aria-selected={who === k} onClick={() => {
                if (k !== who) { setWho(k); setRq(""); setToId(null); setTeamId(null); setService(""); setSubService(null); setConsultTypeId(""); }
              }}>
                {k === "people" ? "People" : k === "teams" ? "Groups" : "Service"}
              </button>
            ))}
          </div>

          {who === "people" && (
            <>
              {recipient && (
                <div className="chips">
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "var(--accent-soft)", color: "var(--accent-ink)", borderRadius: 99, padding: "5px 7px 5px 6px", fontSize: 12.5, fontWeight: 600 }}>
                    <Avatar id={recipient.id} name={recipient.name} size={20} fontSize={10} />{recipient.name}
                    <button onClick={() => setToId(null)} aria-label={`Remove ${recipient.name}`}
                      style={{ background: "none", border: "none", color: "var(--accent-ink)", cursor: "pointer", padding: 0, display: "inline-flex" }}>
                      <Icon name="x" size={13} sw={2.4} />
                    </button>
                  </span>
                </div>
              )}
              {!recipient && (
                <>
                  <div className="search">
                    <span className="ic"><Icon name="search" size={16} /></span>
                    <input type="text" autoComplete="off" value={rq} onChange={(e) => setRq(e.target.value)}
                      placeholder="Search by name, role or service" aria-label="Search physicians on call" />
                  </div>
                  {rq.trim() ? (
                    hits.length === 0
                      ? <div className="hint">No one on call matches “{rq}”. Try the Service tab.</div>
                      : <div role="listbox" aria-label="Search results">
                          {hits.map((e) => <PhysicianOption key={e.id} e={e} label={label(e.service, e.sub_service)} selected={toId === e.id} onSelect={() => pickPerson(e)} />)}
                        </div>
                  ) : (
                    <div className="hint">Search for a physician on call, or switch to Service to see the roster.</div>
                  )}
                </>
              )}
            </>
          )}

          {who === "teams" && (
            teams.length === 0 ? (
              <div className="hint">No groups yet. Make one in the Groups tab, then page it from here.</div>
            ) : (
              <div role="listbox" aria-label="Groups">
                {teams.map((t) => {
                  const n = t.members.filter((m) => m.id !== me.id).length;
                  const on = teamId === t.id;
                  return (
                    <button key={t.id} type="button" className="popt" role="option" aria-selected={on} disabled={n === 0}
                      onClick={() => { setTeamId(on ? null : t.id); setService(on ? "" : t.name); setSubService(null); setToId(null); setConsultTypeId(""); }}>
                      <span className="team-ic"><Icon name="users" size={16} sw={2} /></span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="nm">{t.name}</div>
                        <div className="tt"><span>{n === 0 ? "Nobody else in this group" : `${n} member${n === 1 ? "" : "s"} · ${t.members.filter((m) => m.id !== me.id).map((m) => m.name).slice(0, 3).join(", ")}${n > 3 ? "…" : ""}`}</span></div>
                      </div>
                      <span className={`check${on ? " on" : ""}`} aria-hidden="true">{on && <Icon name="check" size={13} sw={3} />}</span>
                    </button>
                  );
                })}
              </div>
            )
          )}

          {who === "service" && (
            <>
              {service ? (
                <div className="svc-picked">
                  <span className="dot" style={{ background: divColor(service) }} />
                  <span className="nm">{target}</span>
                  <button type="button" className="linkish" onClick={() => pickService("")} aria-label={`Change service, now ${target}`}>Change</button>
                </div>
              ) : (
                <ServicePicker groups={serviceGroups} onCall={onCall} label={label} onPick={(d, sub) => pickService(`${d}${SEP}${sub ?? ""}`)} />
              )}
              {service ? (
                <>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
                    <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "var(--ink)", textTransform: "none", letterSpacing: 0 }}>On call now</h2>
                  </div>
                  {team.length === 0 ? (
                    <div className="gap" role="status">
                      <Icon name="alert" size={15} sw={2} />
                      <div><strong style={{ fontWeight: 600 }}>No one is on call for {target}.</strong> This page will be held for Locating.</div>
                    </div>
                  ) : (
                    <div role="listbox" aria-label={`On-call physicians for ${target}`}>
                      {subService && team.some((e) => e.sub_service !== subService) && (
                        <div className="hint" style={{ marginTop: 0 }}>Nobody is on {subService} itself. The {service} division on call covers it.</div>
                      )}
                      {team.map((e) => <PhysicianOption key={e.id} e={e} label={label(e.service, e.sub_service)} selected={toId === e.id} onSelect={() => setToId(toId === e.id ? null : e.id)} />)}
                    </div>
                  )}
                </>
              ) : null}
            </>
          )}
          {issue("service") && <div className="ferr"><Icon name="alert" size={12} sw={2} />{issue("service")}</div>}
        </div>

        <div className="fsec">
          <span className="flabel">Consult type</span>
          {service ? (
            <div className="chipgrid" role="radiogroup" aria-label="Consult type">
              {consultTypes.map((c) => (
                <button key={c.id} type="button" role="radio" className="chip" aria-checked={consultTypeId === c.id} onClick={() => setConsultTypeId(c.id)}>
                  {consultTypeId === c.id && <Icon name="check" size={13} sw={3} />}{c.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="hint">Pick a recipient or service first.</div>
          )}
          {issue("consultTypeId") && <div className="ferr"><Icon name="alert" size={12} sw={2} />{issue("consultTypeId")}</div>}
        </div>

        <div className="fsec">
          <span className="flabel">Urgency</span>
          <div className="urgseg" role="radiogroup" aria-label="Urgency">
            <button type="button" role="radio" className="routine" aria-checked={urgency === "Routine"} onClick={() => setUrgency("Routine")}>
              {urgency === "Routine" && <Icon name="check" size={12} sw={3} />}Routine
            </button>
            <button type="button" role="radio" className="stat" aria-checked={urgency === "STAT"} onClick={() => setUrgency("STAT")}>
              {urgency === "STAT" && <Icon name="check" size={12} sw={3} />}STAT
            </button>
          </div>
          {urgency === "STAT" && (
            <div className="statnote" role="status">
              <Icon name="alert" size={14} sw={2} />
              <span>STAT pages are for immediate clinical attention. The backup is notified if there is no answer.</span>
            </div>
          )}
        </div>

        {patientData && (
          <>
            <div className="fsec">
              <label htmlFor="question">Clinical question</label>
              <textarea id="question" rows={4} value={question} maxLength={500} onChange={(e) => setQuestion(e.target.value)}
                style={{ height: "auto", minHeight: 96, padding: 10, resize: "vertical", lineHeight: 1.4 }}
                placeholder={consultTypeId === "general" ? "What do you need to ask the on-call physician?" : "The clinical concern, relevant findings, and the question for the receiving physician."} />
            </div>
            {consultTypeId !== "general" && (
              <div className="fsec">
                <span className="flabel">Patient</span>
                <div className="chips" role="radiogroup" aria-label="Identifier type">
                  {(["MRN", "PHN"] as const).map((t) => (
                    <button key={t} type="button" role="radio" className="chip sm" aria-checked={patientIdType === t}
                      onClick={() => { setPatientIdType(t); setPatientId(""); }}>{t}</button>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input className="mono" type="text" inputMode="numeric" value={patientId} style={{ flex: 2 }}
                    maxLength={patientIdType === "PHN" ? 10 : 8} aria-label={`Patient ${patientIdType}`}
                    placeholder={patientIdType === "PHN" ? "PHN · 10 digits" : "MRN · 6–8 digits"}
                    onChange={(e) => setPatientId(e.target.value.replace(/\D/g, ""))} />
                  <input type="text" value={patientInitials} style={{ flex: 1 }} maxLength={8} aria-label="Patient initials"
                    placeholder="Initials" onChange={(e) => setPatientInitials(e.target.value.replace(/[^A-Za-z.]/g, ""))} />
                </div>
              </div>
            )}
          </>
        )}

        {!patientData && (
          <div className="fsec">
            <label htmlFor="context">Context <span style={{ fontWeight: 400, color: "var(--faint)" }}>(optional)</span></label>
            <textarea id="context" rows={3} value={context} maxLength={500} onChange={(e) => setContext(e.target.value)}
              style={{ height: "auto", minHeight: 76, padding: 10, resize: "vertical", lineHeight: 1.4 }}
              placeholder="What the page is about, so they can prepare before calling back." />
            {context.length > 400 && <div className="fine" style={{ textAlign: "left" }}>{500 - context.length} characters left</div>}
          </div>
        )}

        <div className="fsec">
          <label htmlFor="unit">Patient location <span style={{ fontWeight: 400, color: "var(--faint)" }}>(optional)</span></label>
          <div style={{ display: "flex", gap: 8 }}>
            <select id="unit" value={unitId} style={{ flex: 2 }} onChange={(e) => setUnitId(e.target.value)}>
              <option value="">No unit</option>
              {Array.from(new Set((lists?.units ?? []).map((u) => u.grp ?? "Other"))).map((g) => (
                <optgroup key={g} label={g}>
                  {(lists?.units ?? []).filter((u) => (u.grp ?? "Other") === g).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </optgroup>
              ))}
            </select>
            <input className="mono" type="text" value={bed} style={{ flex: 1 }} maxLength={11} aria-label="Bed" placeholder="Bed"
              onChange={(e) => setBed(e.target.value.replace(/[^A-Za-z0-9-]/g, ""))} />
          </div>
          {issue("unitId") && <div className="ferr"><Icon name="alert" size={12} sw={2} />{issue("unitId")}</div>}
          {issue("bed") && <div className="ferr"><Icon name="alert" size={12} sw={2} />{issue("bed")}</div>}
        </div>

        <div className="fsec">
          <label htmlFor="cb">Call you back on</label>
          {(me.callback || me.phoneAlt) && (
            <div className="chips" role="radiogroup" aria-label="Your numbers">
              {([["Primary", me.callback], ["Alternative", me.phoneAlt]] as const).filter(([, n]) => n).map(([k, n]) => (
                <button key={k} type="button" role="radio" className="chip sm" aria-checked={callback === n} onClick={() => setCallback(n!)}>
                  <Icon name="phone" size={12} sw={2.2} />{k} · <span className="mono">{formatPhone(n)}</span>
                </button>
              ))}
            </div>
          )}
          <input id="cb" className="mono" type="tel" inputMode="numeric" value={callback} maxLength={16}
            onChange={(e) => setCallback(e.target.value.replace(/[^0-9+]/g, ""))} placeholder="Extension or phone number" />
          <div className="fine" style={{ textAlign: "left", marginTop: 4 }}>
            Tap one of your numbers, or type another, such as the ward's, when paging on someone's behalf.
          </div>
          {issue("callback") && <div className="ferr"><Icon name="alert" size={12} sw={2} />{issue("callback")}</div>}
        </div>

        {(recipient || gap || group) && (
          <div className="whoinfo">
            <Icon name={gap ? "alert" : group ? "users" : "user"} size={14} sw={2} />
            <div>
              {group
                ? <>Paging all {reach} member{reach === 1 ? "" : "s"} of <b>{group.name}</b> · each gets their own page and answers it</>
                : gap
                ? <>Paging <b>{target}</b> · nobody is on call, so Locating takes it</>
                : <>Paging <b>{recipient!.name}</b> · {recipient!.title} · {recipient!.position === 1 ? "1st call" : "on call"} for {target}
                    {unit ? <> · patient on {unit.short}{bed ? ` bed ${bed}` : ""}</> : null}</>}
            </div>
          </div>
        )}

        <div className="submitbar">
          {!ready && missing.length > 0 && (
            <div className="missing" id="missing"><Icon name="info" size={13} sw={2} /><span>Still needed: {missing.join(", ")}</span></div>
          )}
          <button className={`sendbtn${ready ? " ready" : ""}${urgency === "STAT" ? " stat" : ""}`} onClick={() => void send()}
            disabled={!ready || busy} aria-describedby={!ready ? "missing" : undefined}>
            {urgency === "STAT" && ready && !busy && <Icon name="alert" size={15} sw={2.4} />}{sendLabel}
          </button>
        </div>
      </div>
    </>
  );
}

// Separates division and service in the picker's option values.
const SEP = "\u0001";

/**
 * Who covers a division, or one of its services. A service is covered by its
 * own shift first; with none, by whoever holds the division as a whole. The
 * division alone is covered by everyone on any of its shifts.
 */
function coverFor(onCall: OnCallEntry[], division: string, sub: string | null): OnCallEntry[] {
  const rows = onCall.filter((e) => e.service === division);
  const exact = sub ? rows.filter((e) => e.sub_service === sub) : rows;
  const pick = exact.length > 0 ? exact : sub ? rows.filter((e) => !e.sub_service) : [];
  const seen = new Set<string>();
  return pick.sort((a, b) => a.position - b.position).filter((e) => !seen.has(e.id) && !!seen.add(e.id));
}

function PhysicianOption({ e, label, selected, onSelect }: { e: OnCallEntry; label: string; selected: boolean; onSelect: () => void }) {
  return (
    <button type="button" className="popt" role="option" aria-selected={selected} onClick={onSelect}>
      <Avatar id={e.id} name={e.name} size={34} fontSize={14} bg={selected ? "var(--accent-ink)" : undefined} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="nm">{e.name}</div>
        <div className="tt">
          <span>{e.title}</span>
          <span className={`poschip${e.position === 1 ? " first" : ""}`}>{e.position === 1 ? "1st call" : "On call"}</span>
        </div>
        <div style={{ fontSize: 11.5, color: divColor(e.service), fontWeight: 600, marginTop: 1 }}>{label}</div>
      </div>
      <span className={`check${selected ? " on" : ""}`} aria-hidden="true">{selected && <Icon name="check" size={13} sw={3} />}</span>
    </button>
  );
}

/* Pick a division or one of its services: a combobox. Type to filter; the
 * list opens under the field. Those with someone on call come first, with
 * who holds first call; the rest fold away until asked for or searched. */
type SvcItem = { division: string; sub: string | null; text: string; covered: boolean };
function ServicePicker({ groups, onCall, label, onPick }: {
  groups: { grp: string; items: SvcItem[] }[]; onCall: OnCallEntry[];
  label: (div: string, sub: string | null) => string; onPick: (division: string, sub: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const t = q.trim().toLowerCase();
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    window.addEventListener("pointerdown", away);
    return () => window.removeEventListener("pointerdown", away);
  }, [open]);

  const firstCall = (div: string, sub: string | null) => coverFor(onCall, div, sub).sort((a, b) => a.position - b.position)[0]?.name ?? null;
  // Divisions in order, each followed by its services.
  const all = groups.flatMap((g) => g.items.filter((i) => !i.sub).flatMap((d) => [d, ...g.items.filter((i) => i.sub && i.division === d.division)]));
  const hit = (i: SvcItem) => !t || i.text.toLowerCase().includes(t) || i.division.toLowerCase().includes(t);
  // A division shows if it or any of its services matches; a service shows with its division.
  const divShown = new Set(all.filter(hit).map((i) => i.division));
  const shown = all.filter((i) => divShown.has(i.division) && (!t || !i.sub || hit(i) || hit({ ...i, sub: null, text: i.division })));
  const coveredDiv = (d: string) => shown.some((i) => i.division === d && i.covered);
  const on = shown.filter((i) => coveredDiv(i.division));
  const off = shown.filter((i) => !coveredDiv(i.division));
  const offVisible = t || showAll ? off : [];
  const options = [...on, ...offVisible];
  const pick = (i: SvcItem) => { setOpen(false); setQ(""); onPick(i.division, i.sub); };
  const move = (d: number) => {
    const n = Math.max(0, Math.min(options.length - 1, active + d));
    setActive(n);
    list.current?.querySelector(`#svc-opt-${n}`)?.scrollIntoView({ block: "nearest" });
  };
  const opt = (i: SvcItem, n: number) => {
    const who = firstCall(i.division, i.sub);
    return (
      <li key={`${i.division}${SEP}${i.sub ?? ""}`} id={`svc-opt-${n}`} role="option" aria-selected={n === active}
        className={`svc-opt${i.sub ? " sub" : ""}${n === active ? " active" : ""}`}
        onPointerEnter={() => setActive(n)} onPointerDown={(e) => e.preventDefault()} onClick={() => pick(i)}>
        {!i.sub && <span className="dot" style={{ background: divColor(i.division) }} />}
        <span className="nm">{i.sub ?? i.text}</span>
        <span className={`who${who ? "" : " none"}`}>{who ?? "No one on call"}</span>
      </li>
    );
  };
  return (
    <div className="svc-combo" ref={box}>
      <div className={`svc-field${open ? " open" : ""}`}>
        <Icon name="search" size={17} />
        <input type="text" role="combobox" aria-expanded={open} aria-controls="svc-list" aria-autocomplete="list"
          aria-activedescendant={open && options.length ? `svc-opt-${active}` : undefined}
          aria-label="Division or service" placeholder="Search a division or service" value={q}
          onFocus={() => setOpen(true)} onClick={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setActive(0); setOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); if (!open) setOpen(true); else move(1); }
            else if (e.key === "ArrowUp") { e.preventDefault(); move(-1); }
            else if (e.key === "Enter" && open && options[active]) { e.preventDefault(); pick(options[active]!); }
            else if (e.key === "Escape") setOpen(false);
          }} />
        <button type="button" className="chev" tabIndex={-1} aria-hidden="true" onClick={() => setOpen(!open)}>
          <svg width="12" height="8" viewBox="0 0 12 8" style={{ transform: open ? "rotate(180deg)" : undefined }}><path d="M1 1l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" /></svg>
        </button>
      </div>
      {open && (
        <div className="svc-panel">
          <ul id="svc-list" role="listbox" aria-label="Divisions and services" ref={list}>
            {options.length === 0 && <li className="svc-empty">No division or service matches "{q.trim()}".</li>}
            {on.length > 0 && <li className="svc-head" role="presentation">On call now</li>}
            {on.map((i, n) => opt(i, n))}
            {offVisible.length > 0 && <li className="svc-head" role="presentation">Nobody on call</li>}
            {offVisible.map((i, n) => opt(i, on.length + n))}
          </ul>
          {!t && !showAll && off.length > 0 && (
            <button type="button" className="svc-more" onPointerDown={(e) => e.preventDefault()} onClick={() => setShowAll(true)}>
              Show {off.filter((i) => !i.sub).length} more with nobody on call
            </button>
          )}
        </div>
      )}
    </div>
  );
}
